import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Camera, FlaskConical, Trash2, Upload, X, ChevronDown, ChevronUp, Plus, TrendingUp, TrendingDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import * as pdfjsLib from 'pdfjs-dist';

// Use the same worker setup the Coach page uses for PDF→Image rendering.
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const API = process.env.REACT_APP_BACKEND_URL;

const PANEL_LABELS = {
    blood_count: { de: 'Blutbild', en: 'Blood count' },
    metabolic: { de: 'Stoffwechsel', en: 'Metabolic' },
    lipids: { de: 'Lipide', en: 'Lipids' },
    liver: { de: 'Leber', en: 'Liver' },
    kidney: { de: 'Niere', en: 'Kidney' },
    electrolytes: { de: 'Elektrolyte', en: 'Electrolytes' },
    vitamins: { de: 'Vitamine & Mineralstoffe', en: 'Vitamins & minerals' },
    thyroid: { de: 'Schilddrüse', en: 'Thyroid' },
    inflammation: { de: 'Entzündung', en: 'Inflammation' },
    hormones: { de: 'Hormone', en: 'Hormones' },
    other: { de: 'Sonstige', en: 'Other' },
};

const STATUS_CLASSES = {
    low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 border-blue-300',
    high: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200 border-rose-300',
    normal: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 border-emerald-300',
    unknown: 'bg-muted text-muted-foreground border-border',
};

const STATUS_LABEL = {
    low: { de: 'Niedrig', en: 'Low' },
    high: { de: 'Hoch', en: 'High' },
    normal: { de: 'Normal', en: 'Normal' },
    unknown: { de: '—', en: '—' },
};

const renderPdfFirstPage = async (file) => {
    const arrayBuf = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuf }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 2.0 });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: ctx, viewport }).promise;
    // Return base64 JPEG payload (no data: prefix — backend strips it anyway).
    return canvas.toDataURL('image/jpeg', 0.85);
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

export default function LabValuesTracker() {
    const { language } = useLanguage();
    const isDe = language === 'de';
    const fileInputRef = useRef(null);

    const [markersMeta, setMarkersMeta] = useState({});
    const [latest, setLatest] = useState(null);
    const [series, setSeries] = useState({});
    const [history, setHistory] = useState([]);
    const [freshness, setFreshness] = useState(null);
    const [markerSheet, setMarkerSheet] = useState(null); // { name, value, ... } when editing
    const [editingValue, setEditingValue] = useState('');
    const [markerHistory, setMarkerHistory] = useState({ timeline: [], edits: [] });
    const [extracting, setExtracting] = useState(false);
    const [editing, setEditing] = useState(null); // { measured_at, source, markers: [] }
    const [chartMarker, setChartMarker] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [saving, setSaving] = useState(false);

    const labelFor = useCallback((key) => {
        const meta = markersMeta[key];
        if (meta) return isDe ? meta.de : meta.en;
        // free-text marker: humanise from key
        return key.replace(/_/g, ' ');
    }, [markersMeta, isDe]);

    const panelFor = useCallback((key) => markersMeta[key]?.panel || 'other', [markersMeta]);

    const load = useCallback(async () => {
        try {
            const [metaRes, trendsRes, historyRes, freshRes] = await Promise.all([
                axios.get(`${API}/api/lab-values/markers`),
                axios.get(`${API}/api/lab-values/trends?days=730`),
                axios.get(`${API}/api/lab-values?limit=50`),
                axios.get(`${API}/api/lab-values/freshness`),
            ]);
            setMarkersMeta(metaRes.data?.markers || {});
            setLatest(trendsRes.data?.latest || null);
            setSeries(trendsRes.data?.series || {});
            setHistory(historyRes.data || []);
            setFreshness(freshRes.data || null);
            // Default chart marker = the one with the most data points
            const bestKey = Object.entries(trendsRes.data?.series || {})
                .sort(([, a], [, b]) => (b?.length || 0) - (a?.length || 0))[0]?.[0];
            if (bestKey) setChartMarker(bestKey);
        } catch (e) {
            // Silent — empty state is OK on first load.
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleFile = async (file) => {
        if (!file) return;
        setExtracting(true);
        try {
            let dataUrl;
            if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
                dataUrl = await renderPdfFirstPage(file);
            } else {
                dataUrl = await fileToBase64(file);
            }
            const res = await axios.post(`${API}/api/lab-values/extract`,
                { image_base64: dataUrl, image_type: 'image/jpeg' });
            const measured = res.data?.measured_at_iso || new Date().toISOString();
            setEditing({
                measured_at: measured.slice(0, 16),
                source: res.data?.source || '',
                markers: (res.data?.markers || []).map(m => ({ ...m })),
            });
            toast.success(isDe ? `${res.data?.markers?.length || 0} Marker erkannt` : `${res.data?.markers?.length || 0} markers extracted`, { duration: 4000 });
        } catch (e) {
            toast.error(isDe ? 'Extraktion fehlgeschlagen' : 'Extraction failed', { duration: 4000 });
        } finally {
            setExtracting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const startBlank = () => {
        setEditing({
            measured_at: new Date().toISOString().slice(0, 16),
            source: '',
            markers: [{ name: 'vitamin_d', value: '', unit: 'ng/mL', reference_min: 30, reference_max: 100 }],
        });
    };

    const updateMarker = (idx, patch) => {
        setEditing(prev => {
            if (!prev) return prev;
            const next = { ...prev, markers: prev.markers.map((m, i) => i === idx ? { ...m, ...patch } : m) };
            return next;
        });
    };

    const removeMarker = (idx) => {
        setEditing(prev => prev ? { ...prev, markers: prev.markers.filter((_, i) => i !== idx) } : prev);
    };

    const addMarker = () => {
        setEditing(prev => prev ? { ...prev, markers: [...prev.markers, { name: '', value: '', unit: '' }] } : prev);
    };

    const generateInsights = async () => {
        const rep = history[0];
        if (!rep) return;
        try {
            await axios.post(`${API}/api/lab-values/${rep.id}/insights?language=${language}`);
            setHistory(prev => prev.map((r, i) => i === 0 ? { ...r, insights_status: 'pending' } : r));
        } catch {
            toast.error(isDe ? 'Konnte Tipps nicht starten' : 'Could not start tips', { duration: 4000 });
        }
    };

    const latestReportId = history[0]?.id;
    const latestInsightsStatus = history[0]?.insights_status;
    useEffect(() => {
        if (!latestReportId || latestInsightsStatus !== 'pending') return undefined;
        const t = setInterval(async () => {
            try {
                const r = await axios.get(`${API}/api/lab-values/${latestReportId}/insights`);
                if (r.data.status !== 'pending') {
                    setHistory(prev => prev.map((x, i) => i === 0
                        ? { ...x, insights_status: r.data.status, insights: r.data.insights }
                        : x));
                }
            } catch { /* keep polling */ }
        }, 4000);
        return () => clearInterval(t);
    }, [latestReportId, latestInsightsStatus]);

    const save = async () => {
        if (!editing) return;
        const valid = editing.markers
            .map(m => ({
                ...m,
                value: m.value === '' || m.value === null ? null : parseFloat(m.value),
                reference_min: m.reference_min === '' ? null : (m.reference_min === null ? null : parseFloat(m.reference_min)),
                reference_max: m.reference_max === '' ? null : (m.reference_max === null ? null : parseFloat(m.reference_max)),
            }))
            .filter(m => m.name && m.value !== null && !Number.isNaN(m.value));
        if (valid.length === 0) {
            toast.error(isDe ? 'Mindestens ein Marker mit Wert nötig' : 'At least one marker with a value required', { duration: 4000 });
            return;
        }
        setSaving(true);
        try {
            const payload = {
                measured_at: new Date(editing.measured_at).toISOString(),
                source: editing.source || undefined,
                markers: valid,
            };
            await axios.post(`${API}/api/lab-values?language=${language}`, payload);
            toast.success(isDe ? 'Gespeichert' : 'Saved', { duration: 4000 });
            setEditing(null);
            load();
        } catch (e) {
            toast.error(isDe ? 'Speichern fehlgeschlagen' : 'Save failed', { duration: 4000 });
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm(isDe ? 'Bericht löschen?' : 'Delete report?')) return;
        try {
            await axios.delete(`${API}/api/lab-values/${id}`);
            toast.success(isDe ? 'Gelöscht' : 'Deleted', { duration: 4000 });
            load();
        } catch (e) {
            toast.error(isDe ? 'Löschen fehlgeschlagen' : 'Delete failed', { duration: 4000 });
        }
    };

    // -------- Render helpers --------
    const openMarkerSheet = async (m) => {
        setMarkerSheet({ ...m, latestReportId: latest?.id });
        setEditingValue(String(m.value ?? ''));
        try {
            const res = await axios.get(`${API}/api/lab-values/marker-history/${m.name}`);
            setMarkerHistory({ timeline: res.data?.timeline || [], edits: res.data?.edits || [] });
        } catch {
            setMarkerHistory({ timeline: [], edits: [] });
        }
    };

    const saveMarkerEdit = async () => {
        if (!markerSheet?.latestReportId) return;
        const v = parseFloat(editingValue);
        if (Number.isNaN(v)) {
            toast.error(isDe ? 'Ungültiger Wert' : 'Invalid value', { duration: 4000 });
            return;
        }
        try {
            await axios.patch(
                `${API}/api/lab-values/${markerSheet.latestReportId}/marker/${markerSheet.name}`,
                { value: v, note: 'Manual correction' },
                {},
            );
            toast.success(isDe ? 'Korrigiert' : 'Updated', { duration: 4000 });
            setMarkerSheet(null);
            load();
        } catch {
            toast.error(isDe ? 'Speichern fehlgeschlagen' : 'Save failed', { duration: 4000 });
        }
    };

    const renderMarkerChip = (m, idx, listKey) => {
        const cls = STATUS_CLASSES[m.status || 'unknown'];
        // Delta vs previous measurement (from trend series)
        const pts = series[m.name] || [];
        let delta = null;
        if (pts.length >= 2) {
            const last = pts[pts.length - 1]?.value;
            const prev = pts[pts.length - 2]?.value;
            if (typeof last === 'number' && typeof prev === 'number' && prev !== 0) {
                delta = ((last - prev) / Math.abs(prev)) * 100;
            }
        }
        return (
            <button
                type="button"
                key={`${listKey}-${idx}`}
                data-testid={`lab-marker-${m.name}`}
                onClick={() => openMarkerSheet(m)}
                className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border w-full text-left hover:brightness-95 transition ${cls}`}
            >
                <div className="min-w-0">
                    <p className="text-xs font-semibold truncate">{labelFor(m.name)}</p>
                    <p className="text-[10px] opacity-80">
                        {m.value} {m.unit || ''}
                        {(m.reference_min !== null && m.reference_min !== undefined) || (m.reference_max !== null && m.reference_max !== undefined)
                            ? ` · Ref ${m.reference_min ?? '–'}–${m.reference_max ?? '–'}`
                            : ''}
                    </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {delta !== null && Math.abs(delta) >= 0.05 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold opacity-80" data-testid={`lab-marker-delta-${m.name}`}>
                            {delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {delta > 0 ? '+' : ''}{delta.toFixed(Math.abs(delta) < 10 ? 1 : 0)}%
                        </span>
                    )}
                    <span className="text-[10px] font-semibold uppercase">
                        {STATUS_LABEL[m.status || 'unknown']?.[isDe ? 'de' : 'en']}
                    </span>
                </div>
            </button>
        );
    };

    const latestByPanel = (() => {
        if (!latest?.markers) return {};
        return latest.markers.reduce((acc, m) => {
            const p = m.panel || panelFor(m.name) || 'other';
            (acc[p] ||= []).push(m);
            return acc;
        }, {});
    })();

    const chartData = chartMarker ? (series[chartMarker] || []).map(p => ({
        date: (p.date || '').slice(0, 10),
        value: p.value,
    })) : [];
    const refMin = chartMarker && series[chartMarker]?.[0]?.reference_min;
    const refMax = chartMarker && series[chartMarker]?.[0]?.reference_max;

    return (
        <Card data-testid="lab-values-tracker" className="overflow-hidden">
            <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <FlaskConical className="w-5 h-5 text-purple-600" />
                        <h3 className="font-bold">{isDe ? 'Laborwerte' : 'Lab values'}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,application/pdf"
                            className="hidden"
                            data-testid="lab-upload-input"
                            onChange={(e) => handleFile(e.target.files?.[0])}
                        />
                        <Button
                            size="sm"
                            variant="outline"
                            data-testid="lab-upload-btn"
                            disabled={extracting}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                            <span className="ml-1">{isDe ? 'Befund scannen' : 'Scan report'}</span>
                        </Button>
                        <Button
                            size="sm"
                            data-testid="lab-add-btn"
                            onClick={startBlank}
                        >
                            <Plus className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {/* Stale-banner — soft reminder when the last report is ≥ 6 months old. */}
                {freshness?.stale && (
                    <div
                        className="flex items-start gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50/70 dark:bg-amber-900/15 text-amber-800 dark:text-amber-200"
                        data-testid="lab-stale-banner"
                    >
                        <span className="text-lg leading-none mt-0.5">🐾</span>
                        <div className="text-xs leading-snug">
                            <p className="font-semibold">
                                {isDe
                                    ? `Dein letzter Befund ist ${Math.round((freshness.days_since_latest || 0) / 30)} Monate alt`
                                    : `Your last report is ${Math.round((freshness.days_since_latest || 0) / 30)} months old`}
                            </p>
                            <p className="opacity-90">
                                {isDe
                                    ? 'Viele Werte (Vit D, Ferritin, HbA1c) verändern sich saisonal — Zeit für ein Update?'
                                    : 'Many markers (vit D, ferritin, HbA1c) shift seasonally — time for an update?'}
                            </p>
                        </div>
                    </div>
                )}

                {/* ----- Latest snapshot grouped by panel ----- */}
                {latest && !editing && (
                    <div className="space-y-3" data-testid="lab-latest">
                        <p className="text-xs text-muted-foreground">
                            {isDe ? 'Letzter Bericht' : 'Latest report'} · {latest.measured_at?.slice(0, 10)}
                            {latest.source ? ` · ${latest.source}` : ''}
                        </p>
                        {Object.entries(latestByPanel).map(([panel, ms]) => (
                            <div key={panel}>
                                <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">
                                    {PANEL_LABELS[panel]?.[isDe ? 'de' : 'en'] || panel}
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {ms.map((m, i) => renderMarkerChip(m, i, `latest-${panel}`))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!latest && !editing && (
                    <div className="text-center py-6 text-sm text-muted-foreground">
                        <FlaskConical className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p>{isDe ? 'Noch keine Laborwerte. Lade einen Befund hoch oder gib ihn manuell ein.' : 'No lab values yet. Upload a report or add manually.'}</p>
                    </div>
                )}

                {/* ----- Edit form ----- */}
                {editing && (
                    <div className="space-y-3 border border-dashed border-border rounded-lg p-3" data-testid="lab-edit-form">
                        <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold">{isDe ? 'Bericht bearbeiten' : 'Edit report'}</p>
                            <Button size="sm" variant="ghost" onClick={() => setEditing(null)} data-testid="lab-edit-cancel">
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <Input
                                type="datetime-local"
                                value={editing.measured_at}
                                onChange={(e) => setEditing(p => ({ ...p, measured_at: e.target.value }))}
                                data-testid="lab-edit-date"
                            />
                            <Input
                                placeholder={isDe ? 'Labor / Quelle' : 'Lab / source'}
                                value={editing.source || ''}
                                onChange={(e) => setEditing(p => ({ ...p, source: e.target.value }))}
                                data-testid="lab-edit-source"
                            />
                        </div>
                        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                            {editing.markers.map((m, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-1 items-center" data-testid={`lab-edit-row-${idx}`}>
                                    <Input
                                        className="col-span-4 h-8 text-xs"
                                        list="lab-marker-options"
                                        placeholder={isDe ? 'Marker' : 'Marker'}
                                        value={m.name}
                                        onChange={(e) => updateMarker(idx, { name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                                    />
                                    <Input
                                        className="col-span-2 h-8 text-xs"
                                        type="number"
                                        step="any"
                                        placeholder="Wert"
                                        value={m.value ?? ''}
                                        onChange={(e) => updateMarker(idx, { value: e.target.value })}
                                    />
                                    <Input
                                        className="col-span-2 h-8 text-xs"
                                        placeholder={isDe ? 'Einh.' : 'Unit'}
                                        value={m.unit || ''}
                                        onChange={(e) => updateMarker(idx, { unit: e.target.value })}
                                    />
                                    <Input
                                        className="col-span-1 h-8 text-xs"
                                        type="number"
                                        step="any"
                                        placeholder="Min"
                                        value={m.reference_min ?? ''}
                                        onChange={(e) => updateMarker(idx, { reference_min: e.target.value })}
                                    />
                                    <Input
                                        className="col-span-2 h-8 text-xs"
                                        type="number"
                                        step="any"
                                        placeholder="Max"
                                        value={m.reference_max ?? ''}
                                        onChange={(e) => updateMarker(idx, { reference_max: e.target.value })}
                                    />
                                    <Button
                                        className="col-span-1 h-8 px-2"
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => removeMarker(idx)}
                                        data-testid={`lab-edit-remove-${idx}`}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            ))}
                            <datalist id="lab-marker-options">
                                {Object.keys(markersMeta).map(k => <option key={k} value={k} />)}
                            </datalist>
                        </div>
                        <div className="flex justify-between items-center">
                            <Button size="sm" variant="outline" onClick={addMarker} data-testid="lab-edit-add-marker">
                                <Plus className="w-3.5 h-3.5 mr-1" /> {isDe ? 'Marker hinzufügen' : 'Add marker'}
                            </Button>
                            <Button size="sm" onClick={save} disabled={saving} data-testid="lab-edit-save">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                                {isDe ? 'Speichern' : 'Save'}
                            </Button>
                        </div>
                    </div>
                )}

                {/* ----- Nanouk's lab tips (abnormal markers + progress celebrations) ----- */}
                {!editing && history.length > 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800 p-3 space-y-2" data-testid="lab-insights">
                        <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-bold flex items-center gap-1.5">
                                <span>🐺</span>{isDe ? 'Nanouks Labor-Tipps' : "Nanouk's Lab Tips"}
                            </p>
                            {latestInsightsStatus === 'pending' ? (
                                <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    {isDe ? 'Nanouk analysiert…' : 'Nanouk is analysing…'}
                                </span>
                            ) : (
                                <button
                                    onClick={generateInsights}
                                    className="text-[10px] px-2 py-0.5 rounded-full border border-amber-300 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                                    data-testid="lab-insights-generate"
                                >
                                    {latestInsightsStatus === 'ready' ? (isDe ? '↻ Neu bewerten' : '↻ Re-assess') : (isDe ? 'Tipps generieren' : 'Generate tips')}
                                </button>
                            )}
                        </div>
                        {latestInsightsStatus === 'ready' && (history[0].insights || []).length === 0 && (
                            <p className="text-[11px] text-muted-foreground" data-testid="lab-insights-allgood">
                                {isDe ? 'Alles im grünen Bereich – keine auffälligen Werte! 🎉' : 'Everything in range – no abnormal values! 🎉'}
                            </p>
                        )}
                        {latestInsightsStatus === 'failed' && (
                            <p className="text-[11px] text-muted-foreground">
                                {isDe ? 'Analyse fehlgeschlagen – bitte erneut versuchen.' : 'Analysis failed – please retry.'}
                            </p>
                        )}
                        {(history[0].insights || []).map((ins, i) => (
                            <div
                                key={i}
                                className={`flex gap-2 p-2 rounded-lg text-xs leading-relaxed ${ins.type === 'celebration'
                                    ? 'bg-emerald-100/80 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200'
                                    : 'bg-white dark:bg-card border border-amber-100 dark:border-amber-900'}`}
                                data-testid={`lab-insight-${i}`}
                            >
                                <span className="shrink-0">{ins.type === 'celebration' ? '🎉' : '💡'}</span>
                                <p><span className="font-semibold">{ins.marker}: </span>{ins.text}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* ----- Trend chart (pill picker like Body Composition tracker) ----- */}
                {Object.keys(series).length > 0 && !editing && (
                    <div className="space-y-2" data-testid="lab-trend-chart">
                        <p className="text-xs font-semibold text-muted-foreground">
                            {isDe ? 'Verlauf' : 'Trend'}
                        </p>
                        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto pr-1">
                            {Object.keys(series)
                                .sort((a, b) => (series[b]?.length || 0) - (series[a]?.length || 0))
                                .map(k => (
                                    <button
                                        key={k}
                                        onClick={() => setChartMarker(k)}
                                        data-testid={`lab-trend-pick-${k}`}
                                        className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${chartMarker === k ? 'bg-purple-600 text-white border-purple-600' : 'bg-card border-border text-muted-foreground hover:border-purple-300'}`}
                                    >
                                        {labelFor(k)}{(series[k]?.length || 0) > 1 ? ` · ${series[k].length}` : ''}
                                    </button>
                                ))}
                        </div>
                        {chartData.length > 0 && (
                            <div className="h-40">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData}>
                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                        <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
                                        <Tooltip />
                                        {refMin !== null && refMin !== undefined && refMax !== null && refMax !== undefined && (
                                            <ReferenceArea y1={refMin} y2={refMax} fill="#10b981" fillOpacity={0.08} />
                                        )}
                                        <Line type="monotone" dataKey="value" stroke="#9333ea" strokeWidth={2} dot={{ r: 3 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                )}

                {/* ----- History toggle ----- */}
                {history.length > 1 && !editing && (
                    <div>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setShowHistory(s => !s)}
                            data-testid="lab-history-toggle"
                            className="w-full"
                        >
                            {showHistory ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                            {isDe ? `Verlauf (${history.length})` : `History (${history.length})`}
                        </Button>
                        {showHistory && (
                            <div className="mt-2 space-y-1.5 text-xs">
                                {history.map((r) => (
                                    <div key={r.id} className="flex items-center justify-between py-1 border-b border-border last:border-0">
                                        <span>{r.measured_at?.slice(0, 10)} · {r.markers?.length || 0} {isDe ? 'Werte' : 'values'}{r.source ? ` · ${r.source}` : ''}</span>
                                        <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`lab-history-delete-${r.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </CardContent>

            {/* Marker drill-down sheet — opened by tapping any marker chip.
                Shows the full per-marker timeline + every correction edit. */}
            {markerSheet && (
                <div
                    className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center sm:justify-center"
                    onClick={() => setMarkerSheet(null)}
                    data-testid="lab-marker-sheet-overlay"
                >
                    <div
                        className="bg-card w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl border border-border p-4 max-h-[85vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="lab-marker-sheet"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <p className="text-base font-bold">{labelFor(markerSheet.name)}</p>
                                <p className="text-xs text-muted-foreground">
                                    {STATUS_LABEL[markerSheet.status || 'unknown']?.[isDe ? 'de' : 'en']} ·
                                    Ref {markerSheet.reference_min ?? '–'}–{markerSheet.reference_max ?? '–'} {markerSheet.unit}
                                </p>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => setMarkerSheet(null)} data-testid="lab-marker-sheet-close">
                                <X className="w-4 h-4" />
                            </Button>
                        </div>

                        {/* Inline correction */}
                        <div className="flex items-center gap-2 mb-3">
                            <Input
                                type="number"
                                step="any"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                placeholder={isDe ? 'Korrigierter Wert' : 'Corrected value'}
                                data-testid="lab-marker-sheet-value"
                                className="h-9 text-sm"
                            />
                            <Button size="sm" onClick={saveMarkerEdit} data-testid="lab-marker-sheet-save">
                                {isDe ? 'Korrigieren' : 'Update'}
                            </Button>
                        </div>

                        {/* Mini trend chart for this marker */}
                        {markerHistory.timeline.length >= 2 && (
                            <div className="h-32 mb-3 -mx-1" data-testid="lab-marker-sheet-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={markerHistory.timeline.map(t => ({ date: (t.measured_at || '').slice(0, 10), value: t.value }))}>
                                        <XAxis dataKey="date" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                                        <YAxis tick={{ fontSize: 9 }} domain={['auto', 'auto']} width={34} />
                                        <Tooltip contentStyle={{ fontSize: 11, padding: 6, borderRadius: 8 }} />
                                        {markerSheet.reference_min !== null && markerSheet.reference_min !== undefined
                                            && markerSheet.reference_max !== null && markerSheet.reference_max !== undefined && (
                                            <ReferenceArea y1={markerSheet.reference_min} y2={markerSheet.reference_max} fill="#10b981" fillOpacity={0.08} />
                                        )}
                                        <Line type="monotone" dataKey="value" stroke="#9333ea" strokeWidth={2} dot={{ r: 3 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}

                        {/* Timeline of measurements */}
                        {markerHistory.timeline.length > 0 && (
                            <div className="mb-3">
                                <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">
                                    {isDe ? 'Messverlauf' : 'Measurement timeline'}
                                </p>
                                <div className="space-y-1 text-xs">
                                    {markerHistory.timeline.slice().reverse().map((t, i) => (
                                        <div key={i} className="flex justify-between py-1 border-b border-border last:border-0">
                                            <span>{t.measured_at?.slice(0, 10)}</span>
                                            <span className={`font-semibold ${STATUS_CLASSES[t.status || 'unknown'].split(' ').filter(c => c.startsWith('text-')).join(' ')}`}>
                                                {t.value} {t.unit || ''}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Edit log */}
                        {markerHistory.edits.length > 0 && (
                            <div>
                                <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">
                                    {isDe ? 'Korrekturen' : 'Corrections'} ({markerHistory.edits.length})
                                </p>
                                <div className="space-y-1 text-[11px] text-muted-foreground">
                                    {markerHistory.edits.map((e, i) => (
                                        <div key={i} className="py-1 border-b border-border last:border-0">
                                            <span>{(e.edited_at || '').slice(0, 10)} · {isDe ? 'alt' : 'old'}: {e.old_value}</span>
                                            {e.note && <span className="opacity-80"> · {e.note}</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
}
